import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Dropdown } from 'react-bootstrap';
import classnames from 'classnames';

import Button from '@components/Button';
import Loading from '@components/Loading';

import PrivateSubmissionLayout from '@private-crm-app/components/PrivateSubmissionLayout/PrivateSubmissionLayout';
import PrivateSubmissionInfo from '@private-crm-app/PrivateSubmissionListing/PrivateSubmissionInfo';
import { getSubmissionsList, getFollowedSubmissionsList } from '@private-crm-app/requests/submission';
import {
  createLinkSubmissionRequest,
  findContactsByEmail,
  verifyEmail,
  getAuth0UserContact,
  dismissTransferAlert,
} from '@private-crm-app/requests/user';
import { useUserContext } from '@private-crm-app/user-context';
import { useAuthManagerContext } from '@private-crm-app/auth-manager-context';
import { useAgencyFlags } from '@private-crm-app/hooks/useAgencyFlags';
import { trackViewAllRequests, trackViewClosedRequests, trackViewOpenRequests } from '@private-crm-app/utils';

import { getCRMTokenFromUrlOrStorage } from '@private-crm-app/utils';

import {
  CRM_ISSUE_ID,
  PRIVATE_SUBMISSION_LISTING_TAB_FOLLOWING_VALUE,
  PRIVATE_SUBMISSION_LISTING_TAB_QUERY_KEY,
} from '../constants';

import './style.scss';
const ALERT_TIMEOUT = 5000; // 5 seconds

// Keep in sync with LINK_SUBMISSION_MARKERS in server/api/service/crm/linkSubmissionRequest.ts.
const LINK_SUBMISSION_MARKERS = [
  'will be reviewed for linking to this account',
  'Requests already exist with this contact information',
];

const submissionIsLinkRequest = (description?: string | null) =>
  LINK_SUBMISSION_MARKERS.some((marker) => description?.includes(marker));

// Helper function to get contact search criteria
const getContactSearchCriteria = (user: any) => {
  if (user?.email) {
    return { email: user.email, type: 'email' as const };
  } else if (user?.cellPhone) {
    return { phone: user.cellPhone, type: 'phone' as const };
  }
  return null;
};

export default function PrivateSubmissionListing() {
  const { user, isLoading: isUserLoading, refreshUser } = useUserContext();
  const { auth0User } = useAuthManagerContext();
  const { flags } = useAgencyFlags();
  const isAuthenticated = !!auth0User;
  const linkRequestAcceptedRef = useRef(false);

  const [isLoading, setIsLoading] = useState(true);

  const [verificationState, setVerificationState] = useState({
    isVerifying: false,
    success: false,
    error: '',
    emailSent: false
  });

  // Contact merge toast states
  const [showMergeToast, setShowMergeToast] = useState(false);
  const [isSubmittingNotification, setIsSubmittingNotification] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  // Transfer alert state
  const [showTransferAlert, setShowTransferAlert] = useState(false);

  const [status, setStatus] = useState<string[]>(['OPEN', 'CLOSED']);
  const [submittedSubmissions, setSubmittedSubmissions] = useState<IPrivateCustomerSubmission[]>([]);
  const [followedSubmissions, setFollowedSubmissions] = useState<IPrivateCustomerSubmission[]>([]);
  const [isFollowedLoading, setIsFollowedLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'submitted' | 'following'>('submitted');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  const showTabs = flags.isPublicMapEnabled && isAuthenticated;
  const displaySubmissions = activeTab === 'submitted' ? submittedSubmissions : followedSubmissions;
  const sortedSubmissions = useMemo(() => {
    const list = [...displaySubmissions];
    if (sortOrder === 'oldest') list.reverse();
    return list;
  }, [displaySubmissions, sortOrder]);

  const history = useHistory();
  const location = useLocation();

  // Check for transfer alert when user loads
  useEffect(() => {
    if (user && !isUserLoading) {
      const shouldShow = shouldShowTransferAlert() && flags.isSubmissionTransferredMessageEnabled;
      setShowTransferAlert(shouldShow);
    }
  }, [user, isUserLoading, flags.isSubmissionTransferredMessageEnabled]);

  const clearAlert = useCallback((alertType: 'success' | 'error' | 'emailSent') => {
    setVerificationState(prev => ({ ...prev, [alertType]: false }));
  }, []);

  const hasExistingMergeNotification = (submissions: IPrivateCustomerSubmission[]) =>
    submissions.some((submission) => submissionIsLinkRequest(submission.issueDescription));

  // Helper function to check if transfer alert should be shown
  const shouldShowTransferAlert = () => {
    if (!user?.submissionsTransferredAt) {
      return false;
    }
    
    // If the user has already dismissed the alert, don't show it
    if (user?.submissionsTransferDismissedAt) {
      return false;
    }
    
    // If the alert has more than 30 days, don't show it
    const transferDate = new Date(user.submissionsTransferredAt);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return transferDate > thirtyDaysAgo;
  };

  // Contact detection for merge toast
  useEffect(() => {
    const detectRelatedContacts = async () => {
      if (!user?.id) return;

      // A successful Notify already created the staff request. Do not put the
      // banner back if a list refresh has not returned that submission yet.
      if (linkRequestAcceptedRef.current) {
        setShowMergeToast(false);
        return;
      }

      const searchCriteria = getContactSearchCriteria(user);
      if (!searchCriteria) return;
      
      // FIXED: Check for existing notification BEFORE making API call to prevent flicker
      if (hasExistingMergeNotification(submittedSubmissions)) {
        setShowMergeToast(false);
        console.log('Merge toast prevented - existing notification found in submissions');
        return;
      }
      
      try {
        const contactsResponse = await findContactsByEmail(
          searchCriteria.type === 'email' ? searchCriteria.email : null,
          user.id,
          searchCriteria.type === 'phone' ? searchCriteria.phone : null
        );
        
        if (contactsResponse?.individualContacts?.length > 0) {
          setShowMergeToast(true);
          console.log('Merge toast shown - no existing notification found');
        } else {
          setShowMergeToast(false);
        }
      } catch (error) {
        console.error('Error detecting related contacts:', error);
        setShowMergeToast(false);
      }
    };

    detectRelatedContacts();
  }, [user?.id, user?.email, user?.cellPhone, submittedSubmissions]);



  const handleVerification = useCallback(async (token: string) => {
    setVerificationState(prev => ({ ...prev, isVerifying: true }));
    
    try {
      const response = await verifyEmail(token);
      
      if (response.success) {
        setVerificationState(prev => ({ ...prev, success: true }));
        localStorage.setItem('emailVerificationSuccess', 'true');
        
        // Use email from verification response
        const verifiedEmail = response.email;
        
        if (verifiedEmail && auth0User?.sub) {
          try {
            // First, get the current auth0 user's contact to ensure we're using the correct contact ID
            const authUserResponse = await getAuth0UserContact(verifiedEmail, auth0User.sub);
            
            if (authUserResponse.individualContacts && authUserResponse.individualContacts.length > 0) {
              // CONTACT MERGING DISABLED - const currentUserContact = authUserResponse.individualContacts[0];
              // CONTACT MERGING DISABLED - const currentContactId = currentUserContact.id;
              
              // CONTACT MERGING DISABLED - Now fetch other contacts with the same email (excluding current user)
              // const contactsResponse = await findContactsByEmail(verifiedEmail, currentContactId);
              
              // CONTACT MERGING DISABLED - If there are related contacts with the same email, merge them
              /* 
              if (contactsResponse.individualContacts && contactsResponse.individualContacts.length > 0) {
                const relatedContactIds = contactsResponse.individualContacts.map(contact => contact.id);
                
                // Merge related contacts into current user's contact
                try {
                  await mergeContactsByIds(relatedContactIds, currentContactId);
                  setMergeSuccess({ show: true, count: relatedContactIds.length });
                  
                  // Clear related contacts immediately to prevent verification message from showing again
                  setRelatedContacts([]);
                  
                  setTimeout(() => clearMergeSuccess(), ALERT_TIMEOUT);
                  
                  // Reload the user data and submissions to reflect the merged contacts
                  await refreshUser();
                  
                  // Explicitly reload submissions to reflect any changes after merging contacts
                  getSubmissionsList(status).then((data) => {
                    setSubmittedSubmissions(data.submissions);
                  });
                } catch (mergeError) {
                  // Error handling remains in place but log removed
                }
              } else {
                setRelatedContacts(contactsResponse.individualContacts || []);
              }
              */
            }
          } catch (fetchError) {
            // Error handling remains in place but log removed
          }
        }

        setTimeout(() => clearAlert('success'), ALERT_TIMEOUT);
      }
    } catch (error) {
      setVerificationState(prev => ({
        ...prev,
        error: error.response?.data?.message || 'Email verification failed. Please try again.'
      }));
      setTimeout(() => clearAlert('error'), ALERT_TIMEOUT);
    } finally {
      setVerificationState(prev => ({ ...prev, isVerifying: false }));
      history.replace(location.pathname);
    }
  }, [history, location.pathname, auth0User, clearAlert, status]);

  // CONTACT MERGING DISABLED - Commented out handleButtonClick function
  /*
  const handleButtonClick = useCallback(async () => {
    // Use either user email from context or directly from auth0User if not available yet
    const emailToUse = user?.email || auth0User?.email;
    // Always use auth0User.sub as the definitive source for auth0 user ID
    const auth0SubToUse = auth0User?.sub;
    
    if (!emailToUse) {
      alert('Email address not found');
      return;
    }

    console.log('[CONTACT_DEBUG] handleButtonClick - Attempting to send verification email', {
      emailToUse,
      auth0SubToUse,
      hasUser: !!user,
      isAuth0EmailUser
    });

    setVerificationState(prev => ({ ...prev, isVerifying: true }));
    
    if (isAuth0EmailUser) {
      // For email users, send verification email
      try {
        // Make sure we always use the auth0 sub ID when available
        await sendVerificationEmail(emailToUse, auth0SubToUse);
        console.log('[CONTACT_DEBUG] handleButtonClick - Verification email sent successfully');
        setVerificationState(prev => ({ ...prev, emailSent: true }));
        setTimeout(() => clearAlert('emailSent'), ALERT_TIMEOUT);
      } catch (error) {
        console.error('[CONTACT_DEBUG] handleButtonClick - Error sending verification email:', error);
        const errorMessage = error.response?.data?.message?.includes('already verified') 
          ? 'This email is already verified. No verification email was sent.'
          : 'Failed to send verification email. Please try again later.';
        
        setVerificationState(prev => ({
          ...prev,
          error: errorMessage
        }));
        setTimeout(() => clearAlert('error'), ALERT_TIMEOUT);
      } finally {
        setVerificationState(prev => ({ ...prev, isVerifying: false }));
      }
    } else {
      // CONTACT MERGING DISABLED - For social login users, directly merge the contacts
      try {
        // Get the current user's contact ID
        if (user?.id && auth0SubToUse) {
          // Fetch other contacts with the same email (excluding current user)
          const contactsResponse = await findContactsByEmail(emailToUse, user.id);
          
          if (contactsResponse?.individualContacts?.length > 0) {
            const relatedContactIds = contactsResponse.individualContacts.map(contact => contact.id);
            
            // Merge related contacts into current user's contact
            await mergeContactsByIds(relatedContactIds, user.id);
            setMergeSuccess({ show: true, count: relatedContactIds.length });
            
            // Clear related contacts immediately to prevent verification message from showing again
            setRelatedContacts([]);
            
            setTimeout(() => clearMergeSuccess(), ALERT_TIMEOUT);
            
            // Reload submissions to reflect the merged contacts
            getSubmissionsList(status).then((data) => {
              setSubmittedSubmissions(data.submissions);
            });
          } else {
            setVerificationState(prev => ({
              ...prev,
              error: 'No related contacts found to link'
            }));
            setTimeout(() => clearAlert('error'), ALERT_TIMEOUT);
          }
        }
      } catch (error) {
        console.error('[CONTACT_DEBUG] handleButtonClick - Error linking contacts:', error);
        setVerificationState(prev => ({
          ...prev,
          error: 'Failed to link previous requests. Please try again later.'
        }));
        setTimeout(() => clearAlert('error'), ALERT_TIMEOUT);
      } finally {
        setVerificationState(prev => ({ ...prev, isVerifying: false }));
      }
    }
  }, [user, auth0User, isAuth0EmailUser, clearAlert, clearMergeSuccess, status, setSubmissions]);
  */

  // Contact creation is now handled exclusively in user-context.tsx

  // Clean up CRM issue ID on mount
  useEffect(() => {
    localStorage.removeItem(CRM_ISSUE_ID);
  }, []);

  // Check for stored verification success
  useEffect(() => {
    const storedSuccess = localStorage.getItem('emailVerificationSuccess');
    if (storedSuccess === 'true') {
      setVerificationState(prev => ({ ...prev, success: true }));
      localStorage.removeItem('emailVerificationSuccess');
      setTimeout(() => clearAlert('success'), ALERT_TIMEOUT);
    }
  }, [clearAlert]);

  // Handle email verification
  useEffect(() => {
    const verifyToken = new URLSearchParams(location.search).get('verifyToken');
    if (verifyToken) {
      handleVerification(verifyToken);
    }
  }, [location.search, handleVerification]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get(PRIVATE_SUBMISSION_LISTING_TAB_QUERY_KEY) !== PRIVATE_SUBMISSION_LISTING_TAB_FOLLOWING_VALUE) {
      return;
    }

    if (!showTabs) {
      return;
    }

    setActiveTab('following');
    setStatus(['OPEN', 'CLOSED']);

    params.delete(PRIVATE_SUBMISSION_LISTING_TAB_QUERY_KEY);
    const remaining = params.toString();
    history.replace({
      pathname: location.pathname,
      search: remaining ? `?${remaining}` : '',
      hash: location.hash,
    });
  }, [location.search, showTabs, history, location.pathname, location.hash]);

  // Fetch submissions
  useEffect(() => {
    setIsLoading(true);
    getSubmissionsList(status)
      .then((data) => setSubmittedSubmissions(data.submissions))
      .catch(() => setSubmittedSubmissions([]))
      .finally(() => setIsLoading(false));

    if (!showTabs) return;

    const crmToken = getCRMTokenFromUrlOrStorage();
    if (crmToken) {
      setIsFollowedLoading(true);
      getFollowedSubmissionsList(crmToken, status)
        .then((data) => setFollowedSubmissions(data.submissions))
        .catch(() => setFollowedSubmissions([]))
        .finally(() => setIsFollowedLoading(false));
    } else {
      setFollowedSubmissions([]);
      setIsFollowedLoading(false);
    }
  }, [status, showTabs]);

  // CONTACT MERGING DISABLED - Update contacts when user changes
  // useEffect(() => {
  //   const checkPreviousRequests = async () => {
  //     const emailToCheck = user?.email || auth0User?.email;
  //     
  //     console.log('[CONTACT_DEBUG] checkPreviousRequests - Start', { 
  //       hasEmail: !!emailToCheck, 
  //       isVerifying: verificationState.isVerifying,
  //       userId: user?.id || null
  //     });

  //     if (emailToCheck && !verificationState.isVerifying && auth0User?.sub) {
  //       try {
  //         console.log('[CONTACT_DEBUG] checkPreviousRequests - Finding contacts by email', {
  //           email: emailToCheck,
  //           auth0Sub: auth0User.sub,
  //           userId: user?.id || null
  //         });
  //         
  //         const currentContactResponse = await getAuth0UserContact(emailToCheck, auth0User.sub);

  //         if (currentContactResponse?.individualContacts?.length > 0) {
  //           const currentContactId = currentContactResponse.individualContacts[0].id;

  //           const { individualContacts } = await findContactsByEmail(
  //             emailToCheck,
  //             currentContactId
  //           );

  //           console.log('[CONTACT_DEBUG] checkPreviousRequests - Result', {
  //             hasContacts: !!individualContacts,
  //             contactsCount: individualContacts?.length || 0
  //           });

  //           if (individualContacts && individualContacts.length > 0) {
  //             setRelatedContacts(individualContacts);
  //           } else {
  //             setRelatedContacts([]);
  //           }
  //         } else {
  //           setRelatedContacts([]);
  //         }
  //       } catch (err) {
  //         console.error('[CONTACT_DEBUG] checkPreviousRequests - Error checking previous requests:', err);
  //         setRelatedContacts([]);
  //       }
  //     } else {
  //       console.log('[CONTACT_DEBUG] checkPreviousRequests - Skipped (conditions not met)');
  //     }
  //   };

  //   checkPreviousRequests();
  // }, [user, auth0User, verificationState.isVerifying]);

  // CONTACT MERGING DISABLED - Check if user is from Auth0
  /*
  useEffect(() => {
    console.log('[CONTACT_DEBUG] checkIfUserIsFromAuth0', {
      hasAuth0User: !!auth0User,
      auth0Sub: auth0User?.sub || null
    });
    
    if (auth0User?.sub) {
      const isEmailUser = auth0User.sub.startsWith('auth0');
      console.log('[CONTACT_DEBUG] Setting isAuth0EmailUser to', isEmailUser);
      setIsAuth0EmailUser(isEmailUser);
    }
  }, [auth0User]);
  */

  // Contact creation is now handled exclusively in user-context.tsx
  // This prevents race conditions and duplicate contact creation
  useEffect(() => {
    console.log('[CONTACT_DEBUG] PrivateSubmissionListing - Contact creation delegated to user-context.tsx', { 
      isLoading: isUserLoading, 
      hasUser: !!user, 
      hasAuth0User: !!auth0User,
      auth0Sub: auth0User?.sub,
      userType: auth0User?.sub?.startsWith('sms|') ? 'phone-only' : auth0User?.sub?.startsWith('auth0|') ? 'email' : 'unknown'
    });
  }, [user, auth0User, isUserLoading]);

  if (isLoading) {
    return <Loading show />;
  }

  const onAllStatusClick = () => {
    setStatus(['OPEN', 'CLOSED']);
    trackViewAllRequests(user?.name, user?.email);
  };

  const onOpenStatusClick = () => {
    setStatus(['OPEN']);
    trackViewOpenRequests(user?.name, user?.email);
  };

  const onClosedStatusClick = () => {
    setStatus(['CLOSED']);
    trackViewClosedRequests(user?.name, user?.email);
  };

  // Toast handlers
  const handleNotifyClick = async () => {
    setIsSubmittingNotification(true);
    setVerificationState((prev) => ({ ...prev, error: '' }));
    try {
      const searchCriteria = getContactSearchCriteria(user);

      if (!searchCriteria) {
        throw new Error('No contact information available for notification');
      }

      await createLinkSubmissionRequest();
      linkRequestAcceptedRef.current = true;

      setShowMergeToast(false);
      setShowSuccessToast(true);

      let refreshAttempts = 0;
      const maxAttempts = 3;

      const refreshSubmissions = () => {
        refreshAttempts++;
        getSubmissionsList(status)
          .then((data) => {
            const hasNewNotification = data.submissions.some((submission) =>
              submissionIsLinkRequest(submission.issueDescription)
            );

            setSubmittedSubmissions(data.submissions);

            if (!hasNewNotification && refreshAttempts < maxAttempts) {
              setTimeout(refreshSubmissions, 1000);
            }
          })
          .catch(() => undefined);
      };
      
      // Start refresh after a brief delay
      setTimeout(refreshSubmissions, 500);
      
      // Auto-hide success toast after 5 seconds
      setTimeout(() => {
        setShowSuccessToast(false);
      }, ALERT_TIMEOUT);
      
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create notification. Please try again.';
      setVerificationState((prev) => ({ ...prev, error: message }));
    } finally {
      setIsSubmittingNotification(false);
    }
  };

  const handleDismissMergeToast = () => {
    setShowMergeToast(false);
  };

  const handleDismissSuccessToast = () => {
    setShowSuccessToast(false);
  };

  const handleDismissTransferAlert = async () => {
    setShowTransferAlert(false);
    
    try {
      await dismissTransferAlert(user.id);
      await refreshUser();
    } catch (error) {
      console.error('Failed to dismiss transfer alert:', error);
      // Reshow alert if API call failed
      setShowTransferAlert(true);
    }
  };

  return (
    <PrivateSubmissionLayout>
      <section className="submission-listing" aria-labelledby="page-title">
        <header className="submission-listing__header">
          <h1 id="page-title">My Requests</h1>
        </header>

        {showTabs && (
          <div className="submission-listing__tabs" role="tablist" aria-label="Request list tabs">
            <button
              role="tab"
              aria-selected={activeTab === 'submitted'}
              aria-controls="submission-list-panel"
              id="tab-submitted"
              className={classnames('submission-listing__tab', {
                'submission-listing__tab--active': activeTab === 'submitted',
              })}
              onClick={() => {
                setActiveTab('submitted');
                setStatus(['OPEN', 'CLOSED']);
              }}
            >
              Submitted by me
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'following'}
              aria-controls="submission-list-panel"
              id="tab-following"
              className={classnames('submission-listing__tab', {
                'submission-listing__tab--active': activeTab === 'following',
              })}
              onClick={() => {
                setActiveTab('following');
                setStatus(['OPEN', 'CLOSED']);
              }}
            >
              Following
            </button>
          </div>
        )}

        {/* Contact merge notification (alert style) */}
        {showMergeToast && !showSuccessToast && (
          <div className="submission-listing__alert submission-listing__alert--info">
            <div
              className="alert-content"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fa fa-info-circle"></i>
                <p style={{ margin: 0 }}>
                  Requests already exist with this contact information. If you'd like to link previous
                  requests to this account, please click the Notify button.
                </p>
              </div>
              <div className="alert-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Button
                  content={isSubmittingNotification ? 'Submitting...' : 'Notify'}
                  onClick={handleNotifyClick}
                  bsStyle={ButtonStyle.PRIMARY}
                  disabled={isSubmittingNotification}
                />
                <button
                  type="button"
                  onClick={handleDismissMergeToast}
                  className="close-btn"
                  aria-label="Close"
                  style={{ background: 'transparent', border: 'none' }}
                  disabled={isSubmittingNotification}
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success notification (alert style) */}
        {showSuccessToast && (
          <div className="submission-listing__alert submission-listing__alert--success">
            <div
              className="alert-content"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fa fa-check-circle"></i>
                <p style={{ margin: 0 }}>
                  We’ve received your notification. Past requests for {user?.email || auth0User?.email} will be reviewed for linking to this account.
                </p>
              </div>
              <button
                type="button"
                onClick={handleDismissSuccessToast}
                className="close-btn"
                aria-label="Close"
                style={{ background: 'transparent', border: 'none' }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Transfer notification (alert style) */}
        {showTransferAlert && (
          <div className="submission-listing__alert submission-listing__alert--info">
            <div
              className="alert-content"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fa fa-info-circle"></i>
                <p style={{ margin: 0 }}>
                  Your past submissions have been moved to a different Account as requested. If you have questions, please contact us.
                </p>
              </div>
              <button
                type="button"
                onClick={handleDismissTransferAlert}
                className="close-btn"
                aria-label="Close"
                style={{ background: 'transparent', border: 'none' }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* CONTACT MERGING DISABLED - Email verification sent message
        {verificationState.emailSent && (
          <div className="submission-listing__alert submission-listing__alert--info">
            <div className="alert-content">
              <i className="fa fa-info-circle"></i>
              <p>Verification email sent successfully. Please check your inbox.</p>
            </div>
            <Button 
              content="×" 
              onClick={() => clearAlert('emailSent')} 
              bsStyle={ButtonStyle.DEFAULT}
              className="close-btn" 
            />
          </div>
        )}
        */}

        {/* CONTACT MERGING DISABLED - Email verification success message
        {verificationState.success && (
          <div className="submission-listing__alert submission-listing__alert--success">
            <div className="alert-content">
              <i className="fa fa-check-circle"></i>
              <p>Email verified successfully. Your previous requests have been linked to your account.</p>
            </div>
            <Button 
              content="×" 
              onClick={() => clearAlert('success')} 
              bsStyle={ButtonStyle.DEFAULT}
              className="close-btn" 
            />
          </div>
        )}
        */}

        {/* CONTACT MERGING DISABLED - Merge success message
        {mergeSuccess.show && (
          <div className="submission-listing__alert submission-listing__alert--success">
            <div className="alert-content">
              <i className="fa fa-check-circle"></i>
              <p>Successfully merged. All requests have been linked to your account.</p>
            </div>
            <Button 
              content="×" 
              onClick={clearMergeSuccess} 
              bsStyle={ButtonStyle.DEFAULT}
              className="close-btn" 
            />
          </div>
        )}
        */}

        {verificationState.error && (
          <div className="submission-listing__alert submission-listing__alert--error" role="alert">
            <div className="alert-content">
              <i className="fa fa-exclamation-circle"></i>
              <p>{verificationState.error}</p>
            </div>
            <Button 
              content="×" 
              onClick={() => clearAlert('error')} 
              bsStyle={ButtonStyle.DEFAULT}
              className="close-btn" 
            />
          </div>
        )}

        {/* CONTACT MERGING DISABLED - Message asking user to verify/link previous requests
        {relatedContacts.length > 0 && !mergeSuccess.show && (
          <div className="submission-listing__alert submission-listing__alert--success">
            <div className="alert-content">
              <i className="fa fa-bell"></i>
              <p>Requests already exist with this email address. If you'd like to link previous requests to this account, please click the Verify button and we'll send you a verification link by email.</p>
            </div>
            <Button 
              content={verificationState.isVerifying ? 
                (isAuth0EmailUser ? "Sending..." : "Linking...") : 
                (isAuth0EmailUser ? "Verify" : "Link Previous Requests")} 
              onClick={handleButtonClick} 
              bsStyle={ButtonStyle.SUCCESS}
              disabled={verificationState.isVerifying} 
            />
          </div>
        )}
        */}

        <div className="submission-listing__filter" role="group" aria-label="Filter requests">
          <Button
            content="All"
            bsStyle={status.length > 1 ? ButtonStyle.PRIMARY : ButtonStyle.GHOST}
            onClick={onAllStatusClick}
            aria-pressed={status.length > 1}
            aria-label="Show all requests"
          />
          <Button
            content="Open"
            bsStyle={status.length === 1 && status.includes('OPEN') ? ButtonStyle.PRIMARY : ButtonStyle.GHOST}
            onClick={onOpenStatusClick}
            aria-pressed={status.length === 1 && status.includes('OPEN')}
            aria-label="Show open requests only"
          />
          <Button
            content="Closed"
            bsStyle={status.length === 1 && status.includes('CLOSED') ? ButtonStyle.PRIMARY : ButtonStyle.GHOST}
            onClick={onClosedStatusClick}
            aria-pressed={status.length === 1 && status.includes('CLOSED')}
            aria-label="Show closed requests only"
          />
        </div>
        <div
          id="submission-list-panel"
          role="tabpanel"
          aria-labelledby={activeTab === 'submitted' ? 'tab-submitted' : 'tab-following'}
          className="submission-listing__container"
        >
          <div className="submission-listing__info">
            <span>{displaySubmissions.length} Requests</span>
            {showTabs && (
              <Dropdown
                className="submission-listing__sort-dropdown"
                align="end"
                children={[
                  <Dropdown.Toggle
                    key="submission-sort-toggle"
                    variant="light"
                    className="submission-listing__sort"
                    aria-label="Sort order"
                  >
                    {sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
                  </Dropdown.Toggle>,
                  <Dropdown.Menu key="submission-sort-menu">
                    <Dropdown.Item
                      onClick={() => setSortOrder('newest')}
                      active={sortOrder === 'newest'}
                    >
                      Newest First
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setSortOrder('oldest')}
                      active={sortOrder === 'oldest'}
                    >
                      Oldest First
                    </Dropdown.Item>
                  </Dropdown.Menu>,
                ] as any}
              />
            )}
          </div>
          {activeTab === 'following' && isFollowedLoading ? (
            <Loading show />
          ) : (
            <ul className="submission-listing__list">
              {sortedSubmissions.map((submission) => (
                <PrivateSubmissionInfo
                  submission={submission}
                  key={submission.id ?? submission.submissionNumber}
                  user={user}
                />
              ))}
            </ul>
          )}
          <Button
            content="Submit Request"
            onClick={() => history.push('/private-submission/create')}
            className="create-request-btn"
          />
        </div>
      </section>
    </PrivateSubmissionLayout>
  );
}
